<template>
  <div class="login-page">
    <h1>Login</h1>
    <p>Log in with your Telegram account to continue.</p>
    <div v-if="insideTelegram">
      <p v-if="error" class="error">Error: {{ error }}</p>
      <button v-if="error" type="button" @click="loginInsideTelegram">Retry</button>
      <div v-else>Logging in with Telegram…</div>
    </div>
    <div v-else-if="!botUsername && !error">Loading…</div>
    <p v-if="error && !insideTelegram" class="error">Error: {{ error }}</p>
    <div v-else-if="!insideTelegram && botUsername !== null && !autoLogging && !loginConfig">
      <LoginWidget
        :botUsername="botUsername"
        :redirectUrl="undefined /* use callback instead of redirect */"
        @auth="handleUserAuth"
      />
    </div>
    <div v-else-if="autoLogging">Attempting automatic login…</div>
    <!-- "Log in with Telegram" (OpenID Connect popup), used instead of the legacy widget once the backend has a Client ID. -->
    <div v-if="!insideTelegram && loginConfig" class="telegram-login">
      <button type="button" :disabled="!loginReady || loginBusy" @click="loginWithTelegramPopup">
        Log in with Telegram
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { apiFetch } from '@/api'
import { LoginWidget } from 'vue-tg'
import type { LoginWidgetUser } from 'vue-tg'
import { useToastStore } from '@/stores/toast.ts'
import { isInsideTelegram } from '@/telegramWebApp'
import { LoginCancelledError, loadTelegramLogin, requestTelegramLogin } from '@/telegramLogin'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()
const botUsername = ref<string | null>(null)
const error = ref<string | null>(null)
const autoLogging = ref(false)
const toasts = useToastStore()
// Inside the Mini App the Login Widget can't work, the signed initData logs in instead (see main.ts).
const insideTelegram = isInsideTelegram()

// "Log in with Telegram" for a plain browser, offered when the backend has `TELEGRAM_LOGIN_CLIENT_ID`. The library is
// loaded up front because a popup is only allowed straight from the click.
const loginConfig = ref<{ clientId: number; nonce: string } | null>(null)
const loginReady = ref(false)
const loginBusy = ref(false)

async function prepareTelegramPopup(config: { clientId: number; nonce: string }) {
  loginConfig.value = config
  try {
    await loadTelegramLogin()
    loginReady.value = true
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Could not load the Telegram login.'
  }
}

async function loginWithTelegramPopup() {
  const config = loginConfig.value
  if (!config || loginBusy.value) return
  loginBusy.value = true
  error.value = null
  try {
    const idToken = await requestTelegramLogin({ clientId: config.clientId, nonce: config.nonce })
    await auth.loginWithTelegramLogin(idToken, config.nonce)
    router.push('/countdown')
  } catch (e) {
    if (!(e instanceof LoginCancelledError)) {
      const msg = e instanceof Error ? e.message : 'Login failed.'
      error.value = msg
      toasts.add(msg, 'error', `${e}`)
    }
    // The nonce is only valid for a few minutes, so a retry needs a fresh one.
    try {
      const resp = await apiFetch<{
        telegram_login_client_id?: number | null
        nonce?: string | null
      }>('/auth/config')
      if (resp.ok) {
        const cfg = await resp.json()
        if (cfg.telegram_login_client_id && cfg.nonce) {
          loginConfig.value = { clientId: cfg.telegram_login_client_id, nonce: cfg.nonce }
        }
      }
    } catch {
      // keep the old nonce, the next attempt reports the problem
    }
  } finally {
    loginBusy.value = false
  }
}

function errorMessage(e: unknown, fallback: string): string {
  return (e instanceof Error && e.message) || fallback
}

async function loginInsideTelegram() {
  error.value = null
  try {
    await auth.loginWithWebApp()
    router.push('/countdown')
  } catch (e: unknown) {
    error.value = errorMessage(e, 'Login failed.')
  }
}

export type LoginPayload = LoginWidgetUser | { bot: string | null }

async function doTelegramLogin(payload: LoginPayload) {
  const resp = await apiFetch<{ token?: string; refresh_token?: string | null }>('/auth/telegram', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!resp.ok) {
    const errorBody = await resp.json()
    const details = errorBody.detail ? `:\n${errorBody.detail}` : ''
    toasts.add(`Failed to log in`, 'error', `Status: ${resp.status}${details}`)
    throw new Error(`Login failed (${resp.status}): ${await resp.text()}`)
  }
  const data = await resp.json()
  const token = data?.token
  if (!token) throw new Error('No token received.')
  auth.setSession(token, data.refresh_token)
  await auth.loadMe()
}

async function handleUserAuth(user: LoginWidgetUser) {
  try {
    await doTelegramLogin({ bot: botUsername.value, ...user })
    // Go to personal countdown after login
    router.push('/countdown')
  } catch (e: unknown) {
    const msg = errorMessage(e, 'Login failed.')
    error.value = msg
    toasts.add(msg, 'error', `${e}`)
  }
}

function parseQueryInt(v: string): number | undefined {
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

async function maybeAutoLogin() {
  // Only proceed if Telegram deep login parameters are present
  const q = route.query as Record<string, string>
  const id = parseQueryInt(q.id)
  const auth_date = parseQueryInt(q.auth_date)
  const hash = q.hash as string | undefined
  if (!id || !auth_date || !hash) return
  if (!botUsername.value) return

  autoLogging.value = true
  try {
    const payload = {
      bot: botUsername.value,
      id,
      auth_date,
      hash,
      first_name: (q.first_name as string | undefined) || undefined,
      last_name: (q.last_name as string | undefined) || undefined,
      username: (q.username as string | undefined) || undefined,
      photo_url: (q.photo_url as string | undefined) || undefined,
    }
    await doTelegramLogin(payload)
    // Clean URL and redirect to countdown
    router.push('/countdown')
  } catch (e: unknown) {
    error.value = errorMessage(e, 'Automatic login failed.')
  } finally {
    autoLogging.value = false
  }
}

onMounted(async () => {
  if (insideTelegram) {
    await loginInsideTelegram()
    return
  }
  // fetch bot username from backend
  try {
    const resp = await apiFetch<{
      bot_username?: string | null
      telegram_login_client_id?: number | null
      nonce?: string | null
    }>('/auth/config')
    if (!resp.ok) {
      const errorBody = await resp.json()
      const details = errorBody.detail ? `:\n${errorBody.detail}` : ''
      toasts.add(`Failed to load login configuration`, 'error', `Status: ${resp.status}${details}`)
      error.value = 'Failed to load auth config.'
      throw new Error(`Login failed (${resp.status}): ${await resp.text()}`)
    }
    const cfg = await resp.json()
    botUsername.value = cfg.bot_username || null
    if (!botUsername.value) {
      error.value = 'Bot username not configured.'
      return
    }
    if (cfg.telegram_login_client_id && cfg.nonce) {
      await prepareTelegramPopup({ clientId: cfg.telegram_login_client_id, nonce: cfg.nonce })
    }
    // Attempt automatic login if Telegram LoginUrl appended params
    await maybeAutoLogin()
  } catch {
    error.value = 'Failed to load auth config.'
  }
})
</script>

<style lang="scss" scoped>
.login-page {
  max-width: 480px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--color-background-soft);
  color: var(--color-text);
  border-radius: 1rem;
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  .error {
    color: #c0392b;
    margin-top: 1rem;
  }
  .telegram-login {
    margin-top: 1rem;
  }
  .telegram-login button:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
</style>
